import { Box, ClickAwayListener, Divider, Grow, Paper, Popper, Typography } from '@mui/material';
import { KeyboardEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { SubMenuFlyoutProvider, useFlyoutContext } from './FlyoutContext';
import NestedList from './NestedList';
import SubMenuHeader from './SubMenuHeader';
import { SubMenuProps } from './types';
import { useContainsActiveRoute } from './useContainsActiveRoute';
import { useHoverFlyout } from './useHoverFlyout';

/** Lado da setinha, em px. Metade dela entra por baixo da borda do painel. */
const ARROW_SIZE = 12;

/**
 * Grupo que abre um painel flutuante ao lado, em duas situações.
 *
 * A PARTIR DA COLUNA recolhida: passar o mouse no ícone abre o painel colado
 * nele, com uma setinha apontando. O painel é portalado para fora da gaveta de
 * propósito — a gaveta recolhida tem 55px, e o que é desenhado dentro dela some
 * por baixo da área de conteúdo, que tem z-index maior.
 *
 * EM CASCATA, quando o grupo é filho de outro painel: a linha ganha uma seta à
 * direita e o painel seguinte abre ao lado dela, como no menu do macOS — sem
 * setinha e sem título, porque a linha que ele está ao lado já é o rótulo.
 *
 * O clique ABRE, nunca alterna: em toque o navegador dispara mouseenter antes
 * do click, e alternar faria a batida abrir e fechar no mesmo gesto. Quem fecha
 * é sair com o ponteiro, clicar fora, o Esc ou navegar.
 */
const SubMenuFlyout = (props: SubMenuProps) => {
    const { children, dense, endAdornment, icon, name } = props;
    const translate = useTranslate();
    const parent = useFlyoutContext();
    const headerRef = useRef<HTMLLIElement>(null);
    const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
    const { isOpen, open, close, scheduleClose, cancelClose } = useHoverFlyout();
    const containsActive = useContainsActiveRoute(children);

    const isCascade = parent.insideFlyout;

    // Navegar fecha a cascata inteira, não só o painel onde está o link.
    const closeChain = useCallback(() => {
        close();
        parent.close();
    }, [close, parent]);

    const flyoutValue = useMemo(
        () => ({ insideFlyout: true, close: closeChain }),
        [closeChain]
    );

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            close();
        }
    };

    return (
        <ClickAwayListener onClickAway={close}>
            <div onKeyDown={handleKeyDown}>
                <SubMenuHeader
                    ref={headerRef}
                    open={isOpen}
                    containsActive={containsActive}
                    dense={dense}
                    icon={icon}
                    name={name}
                    variant={isCascade ? 'row' : 'rail'}
                    // Na coluna o adorno sairia cortado, então ele vai no
                    // título do painel; na linha do painel há largura para ele.
                    endAdornment={isCascade ? endAdornment : undefined}
                    onClick={open}
                    onMouseEnter={open}
                    onMouseLeave={scheduleClose}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                />

                <Popper
                    open={isOpen}
                    anchorEl={headerRef.current}
                    placement="right-start"
                    transition
                    modifiers={[
                        // Na cascata o painel precisa limpar a borda do painel
                        // pai, que fica 8px à direita da linha.
                        {
                            name: 'offset',
                            options: { offset: isCascade ? [-8, 14] : [-12, 10] },
                        },
                        // A partir da coluna, virar para a esquerda jogaria o
                        // painel por cima da gaveta. Na cascata virar é o certo
                        // — é o que o macOS faz perto da borda da tela.
                        { name: 'flip', enabled: isCascade },
                        { name: 'preventOverflow', options: { padding: 8 } },
                        ...(isCascade
                            ? []
                            : [
                                // A seta se centra sozinha no ícone,
                                // respeitando a distância mínima do canto.
                                {
                                    name: 'arrow',
                                    options: { element: arrowRef, padding: 14 },
                                },
                            ]),
                    ]}
                    sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                            <Paper
                                elevation={0}
                                onMouseEnter={cancelClose}
                                onMouseLeave={scheduleClose}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 252,
                                    maxWidth: 320,
                                    // Grupo grande (Relatórios tem ~20 subgrupos)
                                    // não pode estourar a janela.
                                    maxHeight: 'calc(100vh - 96px)',
                                    borderRadius: '16px',
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                    boxShadow:
                                        '0 12px 32px rgba(16, 24, 40, 0.12), 0 2px 8px rgba(16, 24, 40, 0.06)',
                                }}
                            >
                                {!isCascade && (
                                    <>
                                        {/*
                                          * O popper escreve `left`, `top` e
                                          * `transform` inline na seta — por isso
                                          * o deslocamento vai na margem, e o giro
                                          * no ::before.
                                          */}
                                        <Box
                                            ref={setArrowRef}
                                            sx={{
                                                position: 'absolute',
                                                width: ARROW_SIZE,
                                                height: ARROW_SIZE,
                                                marginLeft: `${-ARROW_SIZE / 2}px`,
                                                '&::before': {
                                                    content: '""',
                                                    display: 'block',
                                                    width: '100%',
                                                    height: '100%',
                                                    backgroundColor: 'background.paper',
                                                    borderLeft: theme =>
                                                        `1px solid ${theme.palette.divider}`,
                                                    borderBottom: theme =>
                                                        `1px solid ${theme.palette.divider}`,
                                                    borderBottomLeftRadius: '3px',
                                                    transform: 'rotate(45deg)',
                                                },
                                            }}
                                        />
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: 2,
                                                pt: 1.25,
                                                pb: 1,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.08em',
                                                lineHeight: 1.4,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {translate(name)}
                                            {endAdornment}
                                        </Typography>
                                        <Divider />
                                    </>
                                )}

                                <SubMenuFlyoutProvider value={flyoutValue}>
                                    <Box
                                        sx={{
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            minHeight: 0,
                                            borderRadius: 'inherit',
                                        }}
                                    >
                                        <NestedList
                                            dense={dense}
                                            variant="panel"
                                            onNavigate={closeChain}
                                        >
                                            {children}
                                        </NestedList>
                                    </Box>
                                </SubMenuFlyoutProvider>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

export default SubMenuFlyout;
